/**
 * Avatar color palette for consistent user avatar styling across the application.
 *
 * Design Principles:
 * - Brand-aligned colors for visual consistency
 * - Good contrast on both light and dark backgrounds
 * - Deterministic color assignment based on user ID/email for consistency
 */

/**
 * KnitKnot brand colors for avatar backgrounds.
 * Used when generating colored initials avatars.
 */
export const AVATAR_COLORS = [
  '#DF7933', // Primary Orange (brand)
  '#5EADC0', // Light Teal (brand)
  '#1B4363', // Teal Blue (brand)
  '#DA5D50', // Secondary Orange (brand)
  '#1A2B43', // Deep Blue (brand)
  '#AE3029', // Brand Red (accent)
] as const; // gray-300 (subtle but readable)

/**
 * Resolve a consistent identifier for avatar color selection.
 * Uses priority: email → name → fallback
 *
 * This ensures the same user always gets the same avatar color regardless
 * of which context they're viewed in (notifications, threads, hosted site).
 *
 * @param email - User's email address (most reliable identifier)
 * @param name - User's display name (fallback)
 * @param fallback - Default identifier if neither email nor name available
 * @returns Normalized identifier string for color hashing
 */
export function resolveAvatarIdentifier(
  email?: string | null,
  name?: string | null,
  fallback: string = 'unknown'
): string {
  if (email) return email.toLowerCase().trim();
  if (name) return name.toLowerCase().trim();
  return fallback;
}

/**
 * Generate a simple hash from a string.
 * Uses a basic djb2-like algorithm for consistent distribution.
 */
export function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return Math.abs(hash);
}

/**
 * Get a deterministic avatar color based on an identifier string.
 * Returns a hex color from the AVATAR_COLORS palette.
 *
 * @param identifier - Usually user ID, email, or name
 * @returns A color from the AVATAR_COLORS palette
 */
export function getAvatarColor(identifier: string): string {
  const hash = hashString(identifier);
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/**
 * Calculate relative luminance of a hex color for contrast calculation.
 * Uses WCAG formula: https://www.w3.org/WAI/GL/wiki/Relative_luminance
 */
function getLuminance(hex: string): number {
  // Remove # if present
  const cleanHex = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(cleanHex.slice(0, 2), 16) / 255;
  const g = parseInt(cleanHex.slice(2, 4), 16) / 255;
  const b = parseInt(cleanHex.slice(4, 6), 16) / 255;

  // Apply gamma correction
  const [rLinear, gLinear, bLinear] = [r, g, b].map((c) =>
    c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  );

  // Calculate luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
}

/**
 * Get a contrasting text color (black or white) for WCAG-compliant text on a given background.
 * Returns white text for dark backgrounds, black text for light backgrounds.
 *
 * @param backgroundHex - Background color in hex format (e.g., '#ee7213')
 * @returns '#ffffff' for dark backgrounds, '#000000' for light backgrounds
 */
export function getContrastingTextColor(backgroundHex: string): string {
  const luminance = getLuminance(backgroundHex);
  // WCAG recommends 4.5:1 contrast ratio for normal text
  // Using 0.179 as threshold (luminance of ~#888)
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

/**
 * Brand colors interface for workspace-aware avatar colors
 */
export interface WorkspaceBrandColors {
  primary_color?: string | null;
  secondary_color?: string | null;
  accent_color?: string | null;
}
